import { type HTMLAttributes, type Key } from 'react';
import { Autocomplete, Box, IconButton, TextField, Tooltip, type SxProps, type Theme } from '@mui/material';
import { AddRounded, WarningAmberRounded } from '@mui/icons-material';
import { EST_COST_NOTE, isCostEstimated, renderOptionGroup, sectionOf } from '../constants/tracking-groupings';
import { GROUP_OPTION_VALUES, MAX_DIMS_PER_LEVEL, labelForField, levelDims, levelOf } from '../pages/tracking/tracking-page.format';

interface Props {
  label: string;
  level: string;
  onChange: (level: string) => void;
  used?: string[];
  required?: boolean;
  dimLabel?: (dim: string) => string;
  canAdd?: (dims: string[], dim: string) => boolean;
  limitTags?: number;
  autoOpen?: boolean;
  onClose?: () => void;
  sx?: SxProps<Theme>;
}

export default function LevelPicker({ label, level, onChange, used = [], required, dimLabel = labelForField, canAdd = (dims) => dims.length < MAX_DIMS_PER_LEVEL, limitTags, autoOpen, onClose, sx }: Props) {
  const dims = levelDims(level);
  const taken = new Set(used);
  const renderOption = (props: HTMLAttributes<HTMLLIElement> & { key?: Key }, value: string) => {
    const { key, ...rest } = props;
    return (
      <Box component="li" key={key} {...rest} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Box component="span" sx={{ flex: 1, whiteSpace: 'nowrap' }}>{dimLabel(value)}</Box>
        {isCostEstimated(value) && (
          <Box component="span" sx={{ fontSize: '0.65rem', color: 'text.secondary', flexShrink: 0 }}>~cost</Box>
        )}
        {dims.length > 0 && canAdd(dims, value) && (
          <Tooltip title={`Add to ${dims.map(dimLabel).join(' × ')}`} placement="right">
            <IconButton size="small" aria-label={`Add ${dimLabel(value)}`} sx={{ p: 0.25, my: -0.5 }}
              // the row's own click would replace the level instead
              onClick={(e) => { e.stopPropagation(); onChange(levelOf([...dims, value])); }}>
              <AddRounded fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    );
  };
  return (
    <Autocomplete
      multiple
      size="small"
      // No clear-all button: a dimension's chip X removes it, and the last one clears the level.
      disableClearable
      options={GROUP_OPTION_VALUES.filter((o) => !taken.has(o))}
      groupBy={sectionOf}
      renderGroup={renderOptionGroup}
      value={dims}
      filterSelectedOptions
      onChange={(_, next, reason, details) => {
        if (reason === 'selectOption' && details) onChange(details.option);
        else if (next.length || !required) onChange(levelOf(next));
      }}
      getOptionLabel={dimLabel}
      renderOption={renderOption}
      ChipProps={{ size: 'small' }}
      limitTags={limitTags}
      getLimitTagsText={(n) => `+${n}`}
      openOnFocus={autoOpen}
      onClose={onClose}
      sx={sx}
      slotProps={{ popper: { placement: 'bottom-start', sx: { minWidth: 260 } } }}
      renderInput={(params) => (
        <TextField {...params} label={label} autoFocus={autoOpen}
          InputProps={{ ...params.InputProps,
            // chips ride in startAdornment too
            startAdornment: (
              <>
                {dims.some(isCostEstimated) && (
                  <Tooltip title={EST_COST_NOTE}>
                    <WarningAmberRounded sx={{ fontSize: 16, color: 'warning.main', ml: 0.5, flexShrink: 0 }} />
                  </Tooltip>
                )}
                {params.InputProps.startAdornment}
              </>
            ) }} />
      )}
    />
  );
}
